// src/stores/customerStore.ts
import {
  CustomerData,
  CustomersResponse,
  CoreBankingCustomerData,
} from "@/core/customers/entities/Customers";
import { create } from "zustand";
import { StringsKeys } from "@/core/internationalization/strings";
import Exception from "@/core/exceptions/Exception";
import ExceptionService from "@/core/exceptions/exceptionService/ExceptionService";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import GetCustomersUseCase from "@/core/client/useCases/CustomersUseCase/CustomersUseCase";
import SearchCoreBankingCustomersUseCase from "@/core/client/useCases/CustomersUseCase/SearchCoreBankingCustomersUseCase";
import SearchUnifiedUseCase from "@/core/client/useCases/CustomersUseCase/SearchUnifiedUseCase";
import ImportCoreBankingCustomerUseCase from "@/core/client/useCases/CustomersUseCase/ImportCoreBankingCustomerUseCase";
import ToastService from "@/app/shared/services/toastService";
import { ServiceContainer } from "@/core/di";
import {
  UnifiedSearchFilters,
} from "@/core/customers/repositories/CustomersRepository";

interface CustomerState {
  data: CustomersResponse;
  customers: CustomerData[];
  coreBankingCustomers: CoreBankingCustomerData[];
  selectedCustomer?: CustomerData;
  totalElements: number;
  setCustomers: (customers: CustomerData[]) => void;
  addCustomer: (customer: CustomerData) => void;
  selectCustomer: (customer: CustomerData) => void;
  removeCustomer: (id: string) => void;
  updateCustomer: (customer: CustomerData) => void;
  searchFilter: string;
  setSearchFilter: (searchFilter: string) => void;
  accountNumberSearch: string;
  setAccountNumberSearch: (accountNumberSearch: string) => void;
  segmentFilter: string;
  setSegmentFilter: (segmentFilter: string) => void;
  agencyFilter: string;
  setAgencyFilter: (agencyFilter: string) => void;
  riskLevelFilter: string;
  setRiskLevelFilter: (riskLevelFilter: string) => void;
  kycFilter: string;
  setKycFilter: (kycFilter: string) => void;
  rmFilter: string;
  setRmFilter: (rmFilter: string) => void;
  sensitiveFilter: boolean | null;
  setSensitiveFilter: (sensitiveFilter: boolean | null) => void;
  // Periode de creation, au format AAAA-MM-JJ. Chaine vide = borne non posee : les deux bornes
  // sont independantes, une seule suffit a borner la recherche.
  createdFromFilter: string;
  setCreatedFromFilter: (createdFromFilter: string) => void;
  createdToFilter: string;
  setCreatedToFilter: (createdToFilter: string) => void;
  pageFilter: number;
  setPageFilter: (pageFilter: number) => void;
  sizeFilter: number;
  setSizeFilter: (sizeFilter: number) => void;
  resetFilters: () => void;
  loading: boolean;
  failedFetchingCustomersException: Exception | null;
  setFailedFetchingCustomersException: (
    failedFetchingCustomersException: Exception | null,
  ) => void;
  fetchCustomers: (
    translate: (key: StringsKeys) => string,
    name?: string,
    filters?: {
      segment?: string;
      agency?: string;
      riskLevel?: string;
      kyc?: string;
      rm?: string;
    },
    page?: number,
    size?: number,
  ) => Promise<void>;
  searchUnified: (
    translate: (key: StringsKeys) => string,
    filters?: UnifiedSearchFilters,
  ) => Promise<void>;
  searchCustomersCoreBanking: (
    translate: (key: StringsKeys) => string,
    accountNumber: string,
  ) => Promise<void>;
  importCustomerFromCoreBanking: (
    translate: (key: StringsKeys) => string,
    customer: unknown,
  ) => Promise<string | undefined>;
  isImportingClientFromCoreBanking: boolean;
}

const serviceContainer = ServiceContainer.getInstance();
const customersRepository =
  serviceContainer.getRepository("customers");

// Traduit l'exception levée par les use cases clients (message réseau dédié).
const toCustomersException = (
  error: unknown,
  translate: (key: StringsKeys) => string,
): Exception =>
  ExceptionService.handleException<undefined, DefaultExceptionTypes>(
    error as Exception<undefined, DefaultExceptionTypes>,
    translate,
    (exceptionType: DefaultExceptionTypes) => {
      if (exceptionType === DefaultExceptionTypes.NETWORK_ERROR) {
        return translate(StringsKeys.networkError);
      }
      return null;
    },
  );

export const useCustomerStore = create<CustomerState>((set) => ({
  data: {
    code: "00",
    message: "Le traitement s'est déroulé avec succès.",
    data: {
      totalPages: 1,
      totalElements: 1,
      pageable: {
        pageNumber: 0,
        pageSize: 10,
        sort: [],
        offset: 0,
        paged: true,
        unpaged: false,
      },
      size: 10,
      items: [
        {
          id: "",
          businessKey: "",
          fullName: "",
          segment: "",
          agency: "",
          rm: "",
          kycStatus: "",
          riskLevel: "",
          accountNumber: "",
          totalBalance: "",
          updatedAt: "",
          createdAt: "",
          isImported: true,
        },
      ],
      number: 0,
      sort: [],
      first: true,
      last: true,
      numberOfElements: 0,
      empty: false,
    },
  },
  customers: [],
  coreBankingCustomers: [],
  selectedCustomer: undefined,
  totalElements: 0,
  setCustomers: (customers) => set({ customers }),
  addCustomer: (customer) =>
    set((state) => ({ customers: [...state.customers, customer] })),
  selectCustomer: (customer) => set({ selectedCustomer: customer }),
  removeCustomer: (id) =>
    set((state) => ({ customers: state.customers.filter((c) => c.id !== id) })),
  updateCustomer: (updatedCustomer) =>
    set((state) => ({
      customers: state.customers.map((c) =>
        c.id === updatedCustomer.id ? updatedCustomer : c,
      ),
    })),
  searchFilter: "",
  setSearchFilter: (searchFilter) => set({ searchFilter }),
  accountNumberSearch: "",
  setAccountNumberSearch: (accountNumberSearch) => set({ accountNumberSearch }),
  segmentFilter: "",
  setSegmentFilter: (segmentFilter) => set({ segmentFilter }),
  agencyFilter: "",
  setAgencyFilter: (agencyFilter) => set({ agencyFilter }),
  riskLevelFilter: "",
  setRiskLevelFilter: (riskLevelFilter) => set({ riskLevelFilter }),
  kycFilter: "",
  setKycFilter: (kycFilter) => set({ kycFilter }),
  rmFilter: "",
  setRmFilter: (rmFilter) => set({ rmFilter }),
  sensitiveFilter: null,
  setSensitiveFilter: (sensitiveFilter) => set({ sensitiveFilter }),
  createdFromFilter: "",
  setCreatedFromFilter: (createdFromFilter) => set({ createdFromFilter }),
  createdToFilter: "",
  setCreatedToFilter: (createdToFilter) => set({ createdToFilter }),
  pageFilter: 0,
  setPageFilter: (pageFilter) => set({ pageFilter }),
  sizeFilter: 20,
  setSizeFilter: (sizeFilter) => set({ sizeFilter, pageFilter: 0 }),
  resetFilters: () =>
    set({
      searchFilter: "",
      accountNumberSearch: "",
      segmentFilter: "",
      agencyFilter: "",
      riskLevelFilter: "",
      kycFilter: "",
      rmFilter: "",
      sensitiveFilter: null,
      createdFromFilter: "",
      createdToFilter: "",
      pageFilter: 0,
      sizeFilter: 20,
      failedFetchingCustomersException: null,
    }),
  loading: false,
  failedFetchingCustomersException: null,
  setFailedFetchingCustomersException: (failedFetchingCustomersException) =>
    set({ failedFetchingCustomersException }),
  fetchCustomers: async (translate, name, filters, page, size) => {
    try {
      set({
        loading: true,
        failedFetchingCustomersException: null,
      });

      const { segment, agency, riskLevel, kyc, rm } = filters ?? {};

      const data = await new GetCustomersUseCase(customersRepository).execute(
        name ?? "",
        segment ?? "",
        agency ?? "",
        riskLevel ?? "",
        kyc ?? "",
        rm ?? "",
        page ?? 0,
        size ?? 10,
      );

      set({
        data: data,
        customers: data?.data.items,
        totalElements: data?.data.totalElements ?? 0,
        failedFetchingCustomersException: null,
      });
    } catch (error) {
      const exception = toCustomersException(error, translate);
      console.error("Erreur lors du chargement des clients :", exception);
      set({
        customers: [],
        totalElements: 0,
        failedFetchingCustomersException: exception,
      });
    } finally {
      set({ loading: false });
    }
  },
  searchUnified: async (translate, filters) => {
    try {
      set({
        loading: true,
        failedFetchingCustomersException: null,
      });

      const {
        searchFilter,
        accountNumberSearch,
        segmentFilter,
        agencyFilter,
        riskLevelFilter,
        kycFilter,
        rmFilter,
        sensitiveFilter,
        createdFromFilter,
        createdToFilter,
        pageFilter,
        sizeFilter,
      } = useCustomerStore.getState();

      const resolvedSize = filters?.size ?? sizeFilter ?? 20;
      const resolvedPage = filters?.page ?? pageFilter ?? 0;
      const resolvedOffset = filters?.offset ?? resolvedPage;

      const searchFilters: UnifiedSearchFilters = {
        name: filters?.name ?? (searchFilter || undefined),
        filterValue: filters?.filterValue ?? (searchFilter || undefined),
        accountNumber:
          filters?.accountNumber ?? (accountNumberSearch || undefined),
        segment: filters?.segment ?? (segmentFilter || undefined),
        customerType: filters?.customerType ?? (segmentFilter || undefined),
        agency: filters?.agency ?? (agencyFilter || undefined),
        riskLevel: filters?.riskLevel ?? (riskLevelFilter || undefined),
        kyc: filters?.kyc ?? (kycFilter || undefined),
        rm: filters?.rm ?? (rmFilter || undefined),
        sensitive: filters?.sensitive ?? sensitiveFilter,
        createdFrom: filters?.createdFrom ?? (createdFromFilter || undefined),
        createdTo: filters?.createdTo ?? (createdToFilter || undefined),
        page: resolvedPage,
        size: resolvedSize,
        offset: resolvedOffset,
        rows: filters?.rows ?? resolvedSize,
      };

      const data = await new SearchUnifiedUseCase(customersRepository).execute(
        searchFilters,
      );

      set({
        customers: data.data.referentielClients,
        coreBankingCustomers: data.data.coreBankingClients,
        totalElements: data.data.totalElements,
        failedFetchingCustomersException: null,
      });
    } catch (error) {
      const exception = toCustomersException(error, translate);
      console.error("Erreur lors de la recherche unifiée :", exception);
      set({
        customers: [],
        coreBankingCustomers: [],
        totalElements: 0,
        failedFetchingCustomersException: exception,
      });
    } finally {
      set({ loading: false });
    }
  },
  searchCustomersCoreBanking: async (translate, accountNumber) => {
    try {
      set({
        loading: true,
        failedFetchingCustomersException: null,
      });

      const data = await new SearchCoreBankingCustomersUseCase(
        customersRepository,
      ).execute(accountNumber);

      set({
        data,
        customers: data?.data.items,
        totalElements: data?.data.totalElements ?? 0,
        failedFetchingCustomersException: null,
      });
    } catch (error) {
      const exception = toCustomersException(error, translate);
      console.error(
        "Erreur lors de la recherche dans le core banking :",
        exception,
      );
      set({
        customers: [],
        totalElements: 0,
        failedFetchingCustomersException: exception,
      });
    } finally {
      set({ loading: false });
    }
  },
  importCustomerFromCoreBanking: async (
    translate,
    customer,
  ): Promise<string | undefined> => {
    const sanitizedAccountNumber = customer as unknown;

    set({
      isImportingClientFromCoreBanking: true,
      failedFetchingCustomersException: null,
    });

    // Show loading toast
    const loadingToast = ToastService.loading("Importation en cours...", {
      description:
        "L'importation du client est en cours. Cela peut prendre un certain temps.",
    });

    try {
      const response = await new ImportCoreBankingCustomerUseCase(
        customersRepository,
      ).execute(sanitizedAccountNumber);

      // If we reach here, the import was successful
      ToastService.success("Client importé avec succès !", {
        description:
          "Le client a été importé avec succès dans la base de données.",
        id: loadingToast,
      });

      return response;
    } catch (error) {
      const exception = toCustomersException(error, translate);

      // Show error toast with exception message
      let errorMessage =
        "Une erreur inattendue s'est produite lors de l'importation. Veuillez réessayer plus tard.";
      if (
        exception &&
        typeof exception === "object" &&
        "body" in exception &&
        exception.body &&
        typeof exception.body === "object" &&
        "message" in exception.body
      ) {
        errorMessage =
          (exception.body as { message?: string }).message || errorMessage;
      }

      ToastService.error("Erreur lors de l'importation du client", {
        description: errorMessage,
        id: loadingToast,
      });

      console.error(
        "Erreur lors de l'import du client depuis le core banking :",
        exception,
      );
    } finally {
      set({
        isImportingClientFromCoreBanking: false,
      });
    }
  },
  isImportingClientFromCoreBanking: false,
}));
